import React, { useState } from "react";
import { MapPin, Phone, Mail, Send, CheckCircle2 } from "lucide-react";
import TiltCard from "../components/TiltCard";
import PageBanner, { SectionTitle } from "../components/PageBanner";
import HQGallery from "../components/HQGallery";
import { hqContact, pageBanners, colleges } from "../data/mock";
import { useT } from "../i18n";

export default function Contact() {
  const { t } = useT();
  const [form, setForm] = useState({ name: "", phone: "", college: "", message: "" });
  const [sent, setSent] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    const mobile = form.phone.replace(/\s/g, "");
    if (!/^[6-9]\d{9}$/.test(mobile)) { window.alert("Enter a valid 10-digit mobile number."); return; }
    const collegeName = colleges.find(c => c.slug === form.college)?.name || form.college;
    const body = `Name: ${form.name}\nCollege: ${collegeName}\nMobile: ${mobile}\n\n${form.message}`;
    window.location.href = `mailto:md.apmerc@gmail.com?subject=${encodeURIComponent("APMERCL website enquiry")}&body=${encodeURIComponent(body)}`;
    setSent(true);
    setTimeout(() => { setSent(false); setForm({ name: "", phone: "", college: "", message: "" }); }, 3200);
  };

  return (
    <div className="page-enter">
      <PageBanner image={pageBanners.contact} title="Contact Us" subtitle="We'd love to hear from students, faculty, contractors and public representatives." />

      <section style={{ padding: "50px 24px 60px" }}>
        <div style={{ maxWidth: 1300, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 30, alignItems: "start" }} className="contact-grid">
          <div>
            <SectionTitle>Contact Information</SectionTitle>
            <div style={{ display: "grid", gap: 16 }}>
            {[
              { icon: MapPin, title: "Registered Office", body: hqContact.address, tone: "" },
              { icon: MapPin, title: "Directorate", body: hqContact.address2, tone: "icon-tile-yellow" },
              { icon: Phone, title: "Phone", body: hqContact.phone, tone: "icon-tile-red" },
              { icon: Mail, title: "Email", body: hqContact.email, tone: "" },
            ].map((c, i) => (
              <TiltCard key={i} intensity={4}>
                <div style={{ padding: 22, display: "flex", gap: 14, alignItems: "flex-start" }} data-testid={`contact-info-${i}`}>
                  <div className={`icon-tile ${c.tone}`} style={{ width: 44, height: 44, borderRadius: 12, flexShrink: 0 }}>
                    <c.icon size={20} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "#EA580C", fontWeight: 800, letterSpacing: 1 }}>{c.title.toUpperCase()}</div>
                    <div style={{ fontSize: 14, color: "#1C1917", marginTop: 4, fontWeight: 600, lineHeight: 1.5 }}>{c.body}</div>
                  </div>
                </div>
              </TiltCard>
            ))}
            </div>
          </div>

          <div>
            <SectionTitle>Get in Touch With Us!</SectionTitle>
            <div className="panel-orange" style={{ padding: 32 }} data-testid="contact-form-panel">
              {sent ? (
                <div style={{ textAlign: "center", padding: "40px 20px" }} data-testid="contact-success">
                  <div style={{ display: "inline-flex", padding: 20, borderRadius: "50%", background: "#fff", color: "#EA580C", marginBottom: 18 }}>
                    <CheckCircle2 size={42} />
                  </div>
                  <h3 className="font-serif" style={{ fontSize: 24, fontWeight: 700 }}>Message received</h3>
                  <p style={{ fontSize: 14, marginTop: 6, opacity: 0.95 }}>Our team will get back to you within 2 working days.</p>
                </div>
              ) : (
                <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
                  <p style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{t("reach.p")}</p>
                  <label style={{ fontSize: 14, fontWeight: 600 }}>{t("reach.name")}
                    <input data-testid="contact-name-input" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="input-3d" style={{ marginTop: 6, border: "none" }} placeholder="Enter Full Name" />
                  </label>
                  <label style={{ fontSize: 14, fontWeight: 600 }}>{t("reach.college")}
                    <select data-testid="contact-college-select" required value={form.college} onChange={e => setForm({ ...form, college: e.target.value })} className="input-3d" style={{ marginTop: 6, border: "none" }}>
                      <option value="">Select College</option>
                      {colleges.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                    </select>
                  </label>
                  <label style={{ fontSize: 14, fontWeight: 600 }}>{t("reach.phone")}
                    <input data-testid="contact-phone-input" required type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="input-3d" style={{ marginTop: 6, border: "none" }} placeholder="Enter Phone Number" />
                  </label>
                  <label style={{ fontSize: 14, fontWeight: 600 }}>{t("reach.msg")}
                    <textarea data-testid="contact-message-input" required rows={4} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="input-3d" style={{ marginTop: 6, border: "none", resize: "vertical" }} placeholder="Write your message/query here..." />
                  </label>
                  <button type="submit" className="btn-red" data-testid="contact-submit-btn" style={{ marginTop: 6, justifySelf: "center", width: "min(300px, 100%)" }}>{t("btn.submit")} <Send size={14} style={{ marginLeft: 8, verticalAlign: "middle" }} /></button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <HQGallery compact />

      <section style={{ padding: "0 24px 70px" }}>
        <div style={{ maxWidth: 1300, margin: "0 auto" }}>
          <SectionTitle>Locate Us</SectionTitle>
          <div className="premium-card" style={{ overflow: "hidden", padding: 6 }} data-testid="contact-map">
            <iframe title="APMERCL Registered Office — Phy Care Building, IT Park, Mangalagiri" src="https://www.google.com/maps?q=Phy+Care+Building,+IT+Park,+Mangalagiri,+Guntur,+Andhra+Pradesh&output=embed&z=15" width="100%" height="420" style={{ border: 0, borderRadius: 14, display: "block" }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 900px) {
          .contact-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
